'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, User, Home, CheckCircle2, AlertCircle, Trash2, X, Clock, MessageSquare, Phone } from 'lucide-react';
import type { Customer } from '@/interfaces/customerInterface';
import { CustomerDocumentTypeEnum, ResidenceTypeEnum, CustomerTypeEnum } from '@/interfaces/customerInterface';
import { colombiaDepartments } from '@/tools/colombiaDepartments';
import { validatePhone } from '@/tools/validatePhone';
import { NetworkFetch } from '@/network/networkFetch';
import { apiRoutes } from '@/network/apiRoutes';
import { useToastContext } from '@/context/ToastContext';

// Hook para auto-scroll en mobile cuando se enfoca un input
const useAutoScrollOnFocus = () => {
    useEffect(() => {
        const inputs = document.querySelectorAll('input, select, textarea');

        const handleFocus = (e: Event) => {
            const element = e.target as HTMLElement;
            if (window.innerWidth < 640) { // Solo en mobile
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
        };

        inputs.forEach(input => {
            input.addEventListener('focus', handleFocus);
        });

        return () => {
            inputs.forEach(input => {
                input.removeEventListener('focus', handleFocus);
            });
        };
    }, []);
};

type FormStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface FormErrors {
    [key: string]: string;
}

const testing: boolean = false;

const REFERIDOS = {
    AMIGO: 'Amigo',
    FAMILIAR: 'Familiar',
    REDES_SOCIALES: 'Redes Sociales',
    TECH: 'Instalador o Técnico',
    OTRO: 'Otro',
} as const satisfies Record<string, string>;

type ReferidoType = keyof typeof REFERIDOS;
const REFERIDOS_TYPES: Array<ReferidoType> = Object.keys(REFERIDOS) as Array<ReferidoType>;

export default function RegisterWizard() {
    const { error: showError, warning: showWarning } = useToastContext();
    const navigate = useNavigate();

    // Activar auto-scroll en mobile
    useAutoScrollOnFocus();

    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
    const [currentStep, setCurrentStep] = useState<FormStep>(1);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState<string>('');
    const [installerCode, setInstallerCode] = useState<string>('');
    const [installerCodeError, setInstallerCodeError] = useState<string>('');
    const [techName, setTechName] = useState<string>('');
    const [installationTime, setInstallationTime] = useState<'morning' | 'afternoon' | ''>('');
    const [installationDate, setInstallationDate] = useState<string>('');

    const [neighborhoods, setNeighborhoods] = useState<Array<{ name: string }>>([]);
    const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);

    const [formData, setFormData] = useState<Partial<Customer>>({
        fullName: '',
        email: '',
        phone: '',
        mobileNumbers: [],
        customerType: CustomerTypeEnum.NATURAL,
        documentType: CustomerDocumentTypeEnum.CC,
        documentNumber: '',
        birthDate: undefined,
        referido: undefined,
        serviceData: {
            address: '',
            department: '',
            city: '',
            neighborhood: '',
            residenceType: ResidenceTypeEnum.CASA,
            residenceNumber: '',
            referencePoint: '',
            location: {
                latitude: 0,
                longitude: 0,
            },
            installationTime: '',
            installationDate: '',

        },
    });

    // Estado para los campos de fecha separados
    const [dateInputs, setDateInputs] = useState({
        day: '',
        month: '',
        year: '',
    });

    // Fetch neighborhoods on component mount
    useEffect(() => {
        const fetchNeighborhoods = async () => {
            setIsLoadingNeighborhoods(true);
            try {
                const result = await NetworkFetch(apiRoutes.coverageList, {});
                setNeighborhoods(result || []);
            } catch (error) {
                console.error('Error fetching neighborhoods:', error);
                setNeighborhoods([]);
                showError('Error al cargar los corregimientos');
            } finally {
                setIsLoadingNeighborhoods(false);
            }
        };

        fetchNeighborhoods();
    }, []);

    // Validation functions
    const validateDate = (day: string, month: string, year: string): { valid: boolean; error?: string } => {
        // Si todos están vacíos, es error
        if (!day && !month && !year) {
            return { valid: false, error: 'La fecha de nacimiento es requerida' };
        }

        // Si solo algunos están llenos, es error
        if (!day || !month || !year) {
            return { valid: false, error: 'La fecha debe tener día, mes y año completos' };
        }

        const dayNum = parseInt(day, 10);
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);

        // Validar rangos básicos
        if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12 || yearNum < 1900 || yearNum > new Date().getFullYear()) {
            return { valid: false, error: 'Fecha inválida' };
        }

        // Validar día según el mes
        const daysInMonth: number[] = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if ((yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0) {
            daysInMonth[1] = 29; // Año bisiesto
        }

        if (dayNum > (daysInMonth[monthNum - 1] || 31)) {
            return { valid: false, error: 'Fecha inválida' };
        }

        // Validar que no sea una fecha futura
        const inputDate = new Date(yearNum, monthNum - 1, dayNum);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (inputDate > today) {
            return { valid: false, error: 'La fecha no puede ser en el futuro' };
        }

        return { valid: true };
    };

    const updateDateInputs = (field: 'day' | 'month' | 'year', value: string) => {
        // Solo permitir números y limitar la longitud
        let processedValue = value.replace(/\D/g, '');

        if (field === 'day' && processedValue.length > 2) {
            processedValue = processedValue.slice(0, 2);
        } else if (field === 'month' && processedValue.length > 2) {
            processedValue = processedValue.slice(0, 2);
        } else if (field === 'year' && processedValue.length > 4) {
            processedValue = processedValue.slice(0, 4);
        }

        setDateInputs(prev => ({
            ...prev,
            [field]: processedValue,
        }));

        // Limpiar error de fecha cuando el usuario edita
        if (errors.birthDate) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.birthDate;
                return newErrors;
            });
        }
    };

    const validateStep1 = (): boolean => {
        const newErrors: FormErrors = {};

        if (testing)
            return true

        if (!formData.fullName?.trim()) newErrors.fullName = 'El nombre completo es requerido';
        if (!formData.email?.trim()) newErrors.email = 'El email es requerido';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email inválido';
        if (!formData.phone?.trim()) newErrors.phone = 'El teléfono es requerido';
        else if (!validatePhone(formData.phone)) newErrors.phone = 'El teléfono debe tener 10 dígitos válidos';
        if (!formData.documentNumber?.trim()) newErrors.documentNumber = 'El número de documento es requerido';

        // Validar fecha
        const dateValidation = validateDate(dateInputs.day, dateInputs.month, dateInputs.year);
        if (!dateValidation.valid) {
            newErrors.birthDate = dateValidation.error || 'Fecha inválida';
        } else {
            // Convertir los inputs a un Date y guardar en formData
            const day = parseInt(dateInputs.day, 10);
            const month = parseInt(dateInputs.month, 10);
            const year = parseInt(dateInputs.year, 10);
            const birthDate = new Date(year, month - 1, day);
            handleInputChange('birthDate', birthDate);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = (): boolean => {
        const newErrors: FormErrors = {};
        const sd = formData.serviceData;

        if (testing)
            return true

        // Dirección, departamento y ciudad son opcionales
        // Pero corregimiento es REQUERIDO
        if (!sd?.neighborhood?.trim()) newErrors.neighborhood = 'El barrio o corregimiento es requerido';
        if (!sd?.residenceNumber?.trim()) newErrors.residenceNumber = 'El número de residencia es requerido';
        if (!sd?.referencePoint?.trim()) newErrors.referencePoint = 'El punto de referencia es requerido';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep3 = (): boolean => {
        const newErrors: FormErrors = {};
        const { latitude, longitude } = formData.serviceData?.location || {};

        if (latitude === undefined || latitude === 0) newErrors.latitude = 'Latitud requerida';
        if (longitude === undefined || longitude === 0) newErrors.longitude = 'Longitud requerida';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep4 = async (): Promise<boolean> => {
        const newErrors: FormErrors = {};
        // Paso 4 es opcional, pero si selecciona instalador/técnico debe ingresar el código
        if (formData.referido?.type === 'TECH' && installerCode.trim().length <= 7) {
            newErrors.installerCode = 'El código del instalador debe tener al menos 8 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getMinInstallationDate = (): string => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const validateStep5 = (): boolean => {
        const newErrors: FormErrors = {};

        if (!installationTime) newErrors.installationTime = 'Por favor selecciona un horario';
        if (!installationDate) newErrors.installationDate = 'Por favor selecciona una fecha';

        // Validar que la fecha sea desde mañana en adelante
        if (installationDate) {
            const selectedDate = new Date(installationDate);
            selectedDate.setHours(0, 0, 0, 0);
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);

            if (selectedDate < tomorrow) {
                newErrors.installationDate = 'La fecha debe ser desde mañana en adelante';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = async () => {
        if (currentStep === 1 && validateStep1()) {
            setCurrentStep(2);
            setErrors({});
        } else if (currentStep === 2 && validateStep2()) {
            setCurrentStep(3);
            setErrors({});
        } else if (currentStep === 3 && validateStep3()) {
            setCurrentStep(4);
            setErrors({});
        } else if (currentStep === 4 && await validateStep4()) {
            setCurrentStep(5);
            setErrors({});
        } else if (currentStep === 5 && validateStep5()) {
            // Guardar los datos de instalación en formData
            handleInputChange('serviceData.installationTime', installationTime);
            handleInputChange('serviceData.installationDate', installationDate);
            setCurrentStep(6);
            setErrors({});
        } else if (currentStep === 6) {
            handleSubmit();
        }
    };

    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep((currentStep - 1) as FormStep);
            setErrors({});
        }
    };

    const handleInputChange = (field: string, value: any) => {
        if (field.startsWith('serviceData.')) {
            const subField = field.replace('serviceData.', '');
            if (subField.startsWith('location.')) {
                const locField = subField.replace('location.', '');
                setFormData((prev: any) => ({
                    ...prev,
                    serviceData: {
                        ...prev.serviceData,
                        location: {
                            ...(prev.serviceData?.location || {}),
                            [locField]: value,
                        },
                    },
                }));
            } else {
                setFormData((prev: any) => ({
                    ...prev,
                    serviceData: {
                        ...prev.serviceData,
                        [subField]: value,
                    },
                }));
            }
        } else if (field === 'mobileNumbers') {
            setFormData(prev => ({
                ...prev,
                mobileNumbers: value,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleAddMobileNumber = () => {
        setFormData(prev => ({
            ...prev,
            mobileNumbers: [...(prev.mobileNumbers || []), ''],
        }));
    };

    const handleRemoveMobileNumber = (index: number) => {
        setFormData(prev => ({
            ...prev,
            mobileNumbers: (prev.mobileNumbers || []).filter((_, i) => i !== index),
        }));
    };

    const handleUpdateMobileNumber = (index: number, value: string) => {
        // Validar el teléfono mientras se escribe
        const trimmedValue = value.trim();
        if (trimmedValue && !validatePhone(trimmedValue)) {
            // Mostrar error pero permitir seguir escribiendo
            setErrors(prev => ({
                ...prev,
                [`mobileNumber_${index}`]: 'Teléfono inválido',
            }));
        } else {
            // Limpiar error si es válido o está vacío
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`mobileNumber_${index}`];
                return newErrors;
            });
        }
        const newMobileNumbers = [...(formData.mobileNumbers || [])];
        newMobileNumbers[index] = value;
        handleInputChange('mobileNumbers', newMobileNumbers);
    };

    const handleGetCurrentLocation = async () => {
        setIsGettingLocation(true);

        if (!navigator.geolocation) {
            showError('Tu navegador no soporta geolocalización');
            setIsGettingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                handleInputChange('serviceData.location.latitude', latitude);
                handleInputChange('serviceData.location.longitude', longitude);
                setLocationError('');
                setIsGettingLocation(false);
            },
            (error) => {
                console.error('Error getting location:', error);
                let errorMessage = 'No se pudo obtener tu ubicación';

                // Códigos de error de Geolocation API: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
                switch (error.code) {
                    case 1:
                        errorMessage = 'Permiso de ubicación denegado. Actívalo en la configuración del navegador.';
                        break;
                    case 2:
                        errorMessage = 'La información de ubicación no está disponible.';
                        break;
                    case 3:
                        errorMessage = 'La solicitud de ubicación tardó demasiado.';
                        break;
                }

                setLocationError(errorMessage);
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    };

const handleSubmit = async () => {
  // Prevent multiple submissions
  if (isSubmitting) return;

  setIsSubmitting(true);
  try {
    const result = await NetworkFetch(apiRoutes.newCustomer, formData);
    console.log('Submitting form data:', formData);
    // Ir al paso de bienvenida (paso 7)
    setCurrentStep(7);
  } catch (error: any) {
    console.error('Error:', error);
    // Handle JSON parsing error safely
    let errorMessage = 'Error en el registro';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      try {
        const parse = JSON.parse(error);
        errorMessage = parse.message || errorMessage;
      } catch (jsonError) {
        // If JSON parsing fails, use the error string directly
        errorMessage = error;
      }
    }
    showError('Error: ' + errorMessage);
  } finally {
    setIsSubmitting(false);
  }
};

    return (
        <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-slate-50 sm:via-blue-50 sm:to-indigo-50 sm:py-6 sm:px-4 flex flex-col">
            {/* Modal de Términos y Condiciones */}
            {!hasAcceptedTerms && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col animate-in fade-in duration-300 slide-in-from-bottom-5 sm:zoom-in-95">
                        {/* Header */}
                        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl sm:rounded-t-2xl z-10">
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                    ⚠️ Protección de tus datos
                                </h2>
                                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Lee nuestras políticas</p>
                            </div>
                            <button
                                onClick={() => window.history.back()}
                                className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-100 rounded-lg"
                                title="Cerrar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-5 text-gray-700">
                            <section>
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                                        📄 Política de Tratamiento de Datos Personales 👉 <br /> ( Documento completo )
                                    </h3>
                                    <a
                                        href="/Politica_Tratamiento_de_Datos.pdf"
                                        download="Politica_Tratamiento_de_Datos.pdf"
                                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-xs sm:text-sm whitespace-nowrap shadow-md hover:shadow-lg"
                                        title="Descargar documento completo en PDF"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        Descargar
                                    </a>
                                </div>
                                <p className="text-sm leading-relaxed text-gray-700">
                                    ADITUM LATINOAMÉRICA S.A.S. es una empresa comprometida con la protección de tus datos personales conforme a lo establecido en la Ley 1581 de 2012 y su Decreto Reglamentario 1377 de 2013.
                                </p>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base flex items-center gap-2">
                                    📋 Responsables del Tratamiento de Datos
                                </h3>
                                <p className="text-sm leading-relaxed text-gray-700">
                                    ADITUM LATINOAMÉRICA S.A.S. es el responsable del tratamiento de tus datos personales. Somos los encargados de recopilar, procesar y proteger tu información de acuerdo con los términos de esta política.
                                </p>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base flex items-center gap-2">
                                    🎯 Objeto de la Recopilación
                                </h3>
                                <p className="text-sm leading-relaxed text-gray-700 mb-3">
                                    Recopilamos tus datos personales para:
                                </p>
                                <ul className="space-y-1.5 text-sm list-disc list-inside text-gray-700">
                                    <li>Crear y gestionar tu cuenta de usuario</li>
                                    <li>Proporcionar y mejorar nuestros servicios</li>
                                    <li>Comunicarnos contigo sobre actualizaciones y ofertas</li>
                                    <li>Cumplir con obligaciones legales y regulatorias</li>
                                    <li>Prevenir fraudes y garantizar la seguridad</li>
                                    <li>Analizar y mejorar la experiencia del usuario</li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base flex items-center gap-2">
                                    🔒 Datos Recopilados
                                </h3>
                                <p className="text-sm leading-relaxed text-gray-700 mb-3">
                                    Recopilamos información como:
                                </p>
                                <ul className="space-y-1.5 text-sm list-disc list-inside text-gray-700">
                                    <li>Datos de identificación (nombre, documento de identidad)</li>
                                    <li>Información de contacto (correo electrónico, teléfono)</li>
                                    <li>Datos de ubicación (dirección, geolocalización)</li>
                                    <li>Información de uso (historial de navegación, interacciones)</li>
                                    <li>Datos de transacciones y servicios contratados</li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base flex items-center gap-2">
                                    ✅ Tu Consentimiento
                                </h3>
                                <p className="text-sm leading-relaxed text-gray-700">
                                    Al aceptar esta política, otorgas tu consentimiento previo, expreso e informado para el tratamiento de tus datos personales conforme a lo descrito aquí. Puedes retirar tu consentimiento en cualquier momento contactándonos.
                                </p>
                            </section>

                            <section className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-100">
                                <h3 className="font-bold text-gray-900 mb-2 text-sm flex items-center gap-2">
                                    🛡️ Tus Derechos
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Tienes derecho a:
                                </p>
                                <ul className="space-y-1 text-sm list-disc list-inside text-gray-700">
                                    <li>Acceder a tus datos personales</li>
                                    <li>Rectificar información incorrecta</li>
                                    <li>Solicitar la eliminación de tus datos</li>
                                    <li>Oponertes al tratamiento de tus datos</li>
                                    <li>Solicitar restricción del tratamiento</li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base flex items-center gap-2">
                                    📧 Contacto
                                </h3>
                                <p className="text-sm leading-relaxed text-gray-700">
                                    Para ejercer tus derechos o si tienes preguntas sobre esta política, puedes contactarnos a través de nuestros canales de atención al cliente disponibles en nuestra plataforma.
                                </p>
                            </section>
                        </div>

                        {/* Footer */}
                        <div className="fixed bottom-0 left-0 right-0 sm:static flex gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-100 bg-white sm:bg-gray-50 rounded-b-3xl sm:rounded-b-2xl sm:sticky z-30">
                            <button
                                onClick={() => window.history.back()}
                                className="flex-1 py-2.5 sm:py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm active:scale-95 transform duration-200"
                            >
                                Rechazar
                            </button>
                            <button
                                onClick={() => setHasAcceptedTerms(true)}
                                className="flex-1 py-2.5 sm:py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transform duration-200"
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido Principal (solo se muestra después de aceptar términos) */}
            {hasAcceptedTerms && (
                <div className="flex-1 flex flex-col sm:max-w-2xl sm:mx-auto sm:w-full relative">
                    {/* Header with Back Button and Cancel Button */}
                    <div className="sticky top-0 bg-white sm:bg-transparent sm:mb-6 px-4 sm:px-0 py-3 sm:py-0 mb-2 flex items-center justify-between border-b sm:border-0 border-gray-100 z-40">
                        <button
                            onClick={handlePreviousStep}
                            disabled={currentStep === 1}
                            className={`flex-shrink-0 p-2 rounded-lg transition flex items-center justify-center ${currentStep === 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            title="Atrás"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <div className="flex-1 ml-4">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tu Registro</h1>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">{currentStep <= 6 ? `Paso ${currentStep} de 6` : '¡Registro Completado!'}</p>
                        </div>
                        <button
                            onClick={() => window.history.back()}
                            className="flex-shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50 transition p-2 rounded-lg"
                            title="Cancelar"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Progress Indicator - Desktop */}
                    <div className="mb-5 px-4 sm:px-0 hidden sm:block">
                        <div className="flex items-center justify-between">
                            {[1, 2, 3, 4, 5, 6].map((step) => (
                                <React.Fragment key={step}>
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold mb-1.5 transition-all duration-300 text-sm ${currentStep > step
                                                ? 'bg-emerald-500 text-white'
                                                : currentStep === step
                                                    ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                                                    : 'bg-gray-200 text-gray-600'
                                                }`}
                                        >
                                            {currentStep > step ? (
                                                <CheckCircle2 className="w-4 h-4" />
                                            ) : (
                                                step
                                            )}
                                        </div>
                                        <span className={`text-xs font-semibold text-center leading-tight max-w-[70px] ${currentStep === step ? 'text-blue-600' : 'text-gray-600'}`}>
                                            {['Personales', 'Domicilio', 'Ubicación', 'Referidos', 'Instalación', 'Confirmar'][step - 1]}
                                        </span>
                                    </div>
                                    {step < 6 && (
                                        <div className={`flex-grow h-0.5 mx-2 rounded-full transition-all duration-300 ${currentStep > step ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Progress Bar - Mobile */}
                    <div className="sm:hidden px-4 mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-blue-600 to-blue-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${(currentStep / 6) * 100}%` }}
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5 text-center font-medium">
                            {['Datos Personales', 'Domicilio', 'Ubicación', 'Referidos', 'Instalación', 'Confirmar'][currentStep - 1]}
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="flex-1 flex flex-col bg-white sm:rounded-2xl sm:shadow-md sm:p-5 p-4 sm:mb-4 gap-2 sm:gap-3 pb-56 sm:pb-0 overflow-y-auto sm:overflow-visible">
                        {/* Step 1: Personal Data */}
                        {currentStep === 1 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <User className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">Datos Personales</h2>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-900">
                                        Tipo de Cliente *
                                    </label>
                                    <div className="flex gap-4">
                                        {[CustomerTypeEnum.NATURAL, CustomerTypeEnum.JURIDICO].map((type) => (
                                            <label key={type} className="flex items-center gap-3 cursor-pointer group">
                                                <input
                                                    type="radio"
                                                    name="customerType"
                                                    value={type}
                                                    checked={formData.customerType === type}
                                                    onChange={(e) => handleInputChange('customerType', e.target.value as CustomerTypeEnum)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                                />
                                                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition">
                                                    {type === CustomerTypeEnum.NATURAL ? 'Persona Natural' : 'Persona Jurídica'}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <FormField
                                    label="Nombre Completo"
                                    value={formData.fullName || ''}
                                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                                    error={errors.fullName}
                                />

                                <FormField
                                    label="Email"
                                    type="email"
                                    value={formData.email || ''}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    error={errors.email}
                                />

                                <FormField
                                    label="Teléfono con WhatsApp"
                                    value={formData.phone || ''}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    error={errors.phone}
                                />

                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-900">
                                        Otros Números Móviles
                                    </label>
                                    {(formData.mobileNumbers || []).length > 0 && (
                                        <div className="space-y-2">
                                            {(formData.mobileNumbers || []).map((mobile, index) => (
                                                <div key={index}>
                                                    <div className="flex gap-2 items-start">
                                                        <input
                                                            type="tel"
                                                            value={mobile}
                                                            onChange={(e) =>
                                                                handleUpdateMobileNumber(index, e.target.value)
                                                            }
                                                            className={`flex-1 px-3 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition text-sm ${errors[`mobileNumber_${index}`]
                                                                ? 'border-red-500 focus:ring-red-500'
                                                                : 'border-gray-300 focus:ring-blue-500'
                                                                }`}
                                                            placeholder="Ej: 3001234567"
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveMobileNumber(index)}
                                                            className="px-3 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition font-medium text-sm"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    {errors[`mobileNumber_${index}`] && (
                                                        <div className="flex items-center gap-1 mt-1 text-red-600">
                                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                                            <span className="text-xs font-medium">{errors[`mobileNumber_${index}`]}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        onClick={handleAddMobileNumber}
                                        className="w-full py-2.5 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition font-semibold text-sm active:scale-95 transform duration-200"
                                    >
                                        + Agregar teléfono
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                    <FormField
                                        label="Tipo de Documento"
                                        type="select"
                                        value={formData.documentType || CustomerDocumentTypeEnum.CC}
                                        onChange={(e) =>
                                            handleInputChange(
                                                'documentType',
                                                e.target.value as CustomerDocumentTypeEnum
                                            )
                                        }
                                        options={Object.values(CustomerDocumentTypeEnum).map(type => ({
                                            label: type,
                                            value: type,
                                        }))}
                                    />

                                    <FormField
                                        label="Número de Documento"
                                        value={formData.documentNumber || ''}
                                        onChange={(e) =>
                                            handleInputChange('documentNumber', e.target.value)
                                        }
                                        error={errors.documentNumber}
                                    />
                                </div>

                                {/* Fecha de Nacimiento con 3 inputs */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-900 mb-1">
                                        Fecha de Nacimiento
                                    </label>
                                    <div className="flex gap-1.5">
                                        {/* Input Día */}
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                placeholder=""
                                                inputMode="numeric"
                                                maxLength={2}
                                                value={dateInputs.day}
                                                onChange={(e) => updateDateInputs('day', e.target.value)}
                                                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white hover:border-gray-400 text-center font-medium"
                                            />
                                            <p className="text-xs text-gray-500 text-center mt-1">Día</p>
                                        </div>

                                        {/* Separador */}
                                        <div className="flex items-center">
                                            <span className="text-gray-400 font-bold">-</span>
                                        </div>

                                        {/* Input Mes */}
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder=""
                                                maxLength={2}
                                                value={dateInputs.month}
                                                onChange={(e) => updateDateInputs('month', e.target.value)}
                                                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white hover:border-gray-400 text-center font-medium"
                                            />
                                            <p className="text-xs text-gray-500 text-center mt-1">Mes</p>
                                        </div>

                                        {/* Separador */}
                                        <div className="flex items-center">
                                            <span className="text-gray-400 font-bold">-</span>
                                        </div>

                                        {/* Input Año */}
                                        <div className="flex-1.5">
                                            <input
                                                inputMode="numeric"
                                                type="text"
                                                placeholder=""
                                                maxLength={4}
                                                value={dateInputs.year}
                                                onChange={(e) => updateDateInputs('year', e.target.value)}
                                                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white hover:border-gray-400 text-center font-medium"
                                            />
                                            <p className="text-xs text-gray-500 text-center mt-1">Año</p>
                                        </div>
                                    </div>

                                    {/* Mensaje de error */}
                                    {errors.birthDate && (
                                        <div className="flex items-center gap-1 mt-1.5 text-red-600">
                                            <span className="text-xs font-semibold">⚠ {errors.birthDate}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2: Service Data */}
                        {currentStep === 2 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Home className="w-5 h-5 text-green-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">Cuéntame: en donde Vives</h2>
                                </div>

                                <FormField
                                    label="Dirección Completa (Opcional)"
                                    value={formData.serviceData?.address || ''}
                                    onChange={(e) => handleInputChange('serviceData.address', e.target.value)}
                                    error={errors.address}
                                />

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <FormField
                                        label="Departamento (Opcional)"
                                        type="select"
                                        value={formData.serviceData?.department || ''}
                                        onChange={(e) => {
                                            handleInputChange('serviceData.department', e.target.value);
                                            // Reset city when department changes
                                            handleInputChange('serviceData.city', '');
                                        }}
                                        options={colombiaDepartments.map(dept => ({
                                            label: dept.name,
                                            value: dept.name,
                                        }))}
                                    />

                                    <FormField
                                        label="Ciudad o Municipio (Opcional)"
                                        type="select"
                                        value={formData.serviceData?.city || ''}
                                        onChange={(e) =>
                                            handleInputChange('serviceData.city', e.target.value)
                                        }
                                        options={
                                            formData.serviceData?.department
                                                ? (colombiaDepartments.find(
                                                    dept => dept.name === formData.serviceData?.department
                                                )?.cities || []).map(city => ({
                                                    label: city,
                                                    value: city,
                                                }))
                                                : []
                                        }
                                        disabled={!formData.serviceData?.department}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <FormField
                                        label="Corregimiento *"
                                        type="select"
                                        value={formData.serviceData?.neighborhood || ''}
                                        onChange={(e) =>
                                            handleInputChange('serviceData.neighborhood', e.target.value)
                                        }
                                        error={errors.neighborhood}
                                        options={neighborhoods.map(neighborhood => ({
                                            label: neighborhood.name,
                                            value: neighborhood.name,
                                        }))}
                                    />

                                    <FormField
                                        label="Tipo de Vivienda"
                                        type="select"
                                        value={formData.serviceData?.residenceType || ResidenceTypeEnum.CASA}
                                        onChange={(e) =>
                                            handleInputChange(
                                                'serviceData.residenceType',
                                                e.target.value as ResidenceTypeEnum
                                            )
                                        }
                                        options={Object.values(ResidenceTypeEnum).map(type => ({
                                            label: type,
                                            value: type,
                                        }))}
                                    />
                                </div>

                                <FormField
                                    label="Número de Casa/Apartamento."
                                    value={formData.serviceData?.residenceNumber || ''}
                                    onChange={(e) =>
                                        handleInputChange('serviceData.residenceNumber', e.target.value)
                                    }
                                    error={errors.residenceNumber}
                                />

                                <FormField
                                    label="Barrio o punto de referencia."
                                    value={formData.serviceData?.referencePoint || ''}
                                    onChange={(e) =>
                                        handleInputChange('serviceData.referencePoint', e.target.value)
                                    }
                                    error={errors.referencePoint}
                                />
                            </div>
                        )}

                        {/* Step 3: Geolocation */}
                        {currentStep === 3 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <MapPin className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">Compartenos la ubicación del predio.</h2>
                                </div>

                                <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-3 space-y-2">
                                    <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                                        <span className="text-lg">📍</span>   Esto nos ayuda a verificar cobertura y planificar la instalación.
                                    </p>

                                    <div className="bg-white rounded p-2 text-xs text-blue-700 space-y-0.5">
                                        <p className="font-semibold">Al hacer clic:</p>
                                        <p>✓ Tu navegador pedirá permiso de ubicación</p>
                                        <p>✓ Completaremos automáticamente las coordenadas</p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleGetCurrentLocation}
                                    disabled={isGettingLocation}
                                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-95 transform duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <MapPin className="w-4 h-4" />
                                    {isGettingLocation ? 'Obteniendo ubicación...' : 'Obtener Ubicación'}
                                </button>

                                {locationError && (
                                    <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-red-900">Error de ubicación</p>
                                            <p className="text-sm text-red-800">{locationError}</p>
                                        </div>
                                        <button
                                            onClick={() => setLocationError('')}
                                            className="ml-auto text-red-600 hover:text-red-800"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}

                                {!formData.serviceData?.location?.latitude && !locationError && errors.latitude && (
                                    <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-red-900">Ubicación requerida</p>
                                            <p className="text-sm text-red-800">Por favor, haz clic en el botón para obtener tu ubicación.</p>
                                        </div>
                                    </div>
                                )}

                                {formData.serviceData?.location?.latitude !== 0 &&
                                    formData.serviceData?.location?.longitude !== 0 && (
                                        <div className="space-y-2">
                                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex items-center gap-2">
                                                <span className="text-lg">✓</span>
                                                <div>
                                                    <p className="text-xs font-semibold text-emerald-900">Ubicación confirmada</p>
                                                    <p className="text-xs text-emerald-700">{formData.serviceData.location.latitude.toFixed(6)}, {formData.serviceData.location.longitude.toFixed(6)}</p>
                                                </div>
                                            </div>

                                            {/* Google Maps Embed */}
                                            <div className="rounded-lg overflow-hidden border border-gray-300 shadow-md">
                                                <iframe
                                                    width="100%"
                                                    height="220"
                                                    style={{ border: 0 }}
                                                    loading="lazy"
                                                    allowFullScreen
                                                    referrerPolicy="no-referrer-when-downgrade"
                                                    src={`https://maps.google.com/maps?q=${formData.serviceData.location.latitude},${formData.serviceData.location.longitude}&output=embed&z=17`}
                                                ></iframe>
                                            </div>

                                            <p className="text-xs text-gray-500 text-center">
                                                Verifica que sea el lugar correcto.
                                            </p>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Step 4: Referidos */}
                        {currentStep === 4 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-amber-100 rounded-lg">
                                        <User className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">¿Cómo nos conociste? (Opcional)</h2>
                                </div>

                                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3 space-y-2">
                                    <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                        <span className="text-lg">👥</span> Cuéntanos, como te enteraste de los servicios de Aditum.
                                    </p>
                                    <p className="text-sm text-amber-800">
                                        Si alguien te recomendó nuestros servicios, queremos saberlo.
                                    </p>
                                </div>

                                <FormField
                                    label="¿Cómo nos conociste?"
                                    type="select"
                                    value={formData.referido?.type || ''}
                                    onChange={(e) => {
                                        const value = e.target.value as ReferidoType;
                                        if (value) {
                                            handleInputChange('referido', { type: value, data: value === 'TECH' ? installerCode : '' });
                                        } else {
                                            handleInputChange('referido', undefined);
                                        }
                                        // Reset installer code if not selected
                                        if (value !== 'TECH') {
                                            setInstallerCode('');
                                            setInstallerCodeError('');
                                        }
                                    }}
                                    options={REFERIDOS_TYPES.map(type => ({
                                        label: REFERIDOS[type],
                                        value: type,
                                    }))}
                                />

                                {formData.referido?.type === 'TECH' && (
                                    <div className="space-y-2 pt-2 border-t border-gray-200">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                                            <p className="text-xs text-blue-800">
                                                <span className="font-semibold">💡 Importante:</span> Ingresa el código del instalador o técnico que te recomendó. Esto nos permitirá procesar correctamente tu referencia.
                                            </p>
                                        </div>
                                        <FormField
                                            label="Código del Instalador/Técnico"
                                            value={installerCode}
                                            onChange={async (e) => {
                                                const value = e.target.value.trim().toUpperCase();

                                                // Actualizar el estado mientras escriben
                                                if (value.length <= 8) {
                                                    setInstallerCode(value);

                                                    // Si el código tiene exactamente 8 caracteres, validar en el servidor
                                                    if (value.length === 8) {
                                                        try {
                                                            const techData = await NetworkFetch(apiRoutes.techGet, { query: { reference: value } });

                                                            if (techData && techData.length > 0) {
                                                                const tech = techData[0];
                                                                // Código válido - limpiar errores y actualizar referido
                                                                setInstallerCodeError('');
                                                                setTechName(tech.name || '');
                                                                handleInputChange('referido', {
                                                                    type: 'TECH',
                                                                    data: value
                                                                });
                                                            } else {
                                                                // Código inválido
                                                                setInstallerCodeError('Código de instalador/técnico no válido');
                                                                setTechName('');
                                                            }
                                                        } catch (error) {
                                                            setInstallerCodeError('Error al validar el código. Intenta de nuevo.');
                                                            setTechName('');
                                                            console.error('Error validating installer code:', error);
                                                        }
                                                    } else if (value.length < 8) {
                                                        // Limpiar error mientras escriben código incompleto
                                                        setInstallerCodeError('');
                                                        setTechName('');
                                                    }
                                                }
                                            }}
                                            error={errors.installerCode || installerCodeError}
                                            placeholder="Ej: ADT1725"
                                        />

                                        {/* Mostrar nombre del técnico/instalador cuando es válido */}
                                        {techName && !installerCodeError && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center gap-2">
                                                <span className="text-lg">✓</span>
                                                <div>
                                                    <p className="text-xs font-semibold text-green-900">Técnico/Instalador verificado</p>
                                                    <p className="text-sm text-green-700 font-medium">{techName}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.referido && formData.referido.type !== 'TECH' && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center gap-2">
                                        <span className="text-lg">✓</span>
                                        <div>
                                            <p className="text-xs font-semibold text-green-900">Referencia registrada</p>
                                            <p className="text-xs text-green-700">{REFERIDOS[formData.referido.type as ReferidoType]}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 5: Installation Time and Date */}
                        {currentStep === 5 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-orange-100 rounded-lg">
                                        <Clock className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">Agendar Instalación</h2>
                                </div>

                                <div className="bg-orange-50 border-l-4 border-orange-400 rounded-lg p-3 space-y-2">
                                    <p className="text-sm font-semibold text-orange-900 flex items-center gap-2">
                                        <span className="text-lg">📅</span> Selecciona tu horario y fecha preferida para la instalación.
                                    </p>
                                    <p className="text-sm text-orange-800">
                                        Nuestro equipo de técnicos se adaptará a tu disponibilidad.
                                    </p>
                                </div>

                                {/* Horario Selection */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-900">
                                        Horario de Instalación *
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => {
                                                setInstallationTime('morning');
                                                if (errors.installationTime) {
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.installationTime;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            className={`py-3 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition border-2 ${installationTime === 'morning'
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                                }`}
                                        >
                                            <span className="text-lg">🌅</span> Mañana
                                        </button>
                                        <button
                                            onClick={() => {
                                                setInstallationTime('afternoon');
                                                if (errors.installationTime) {
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.installationTime;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            className={`py-3 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition border-2 ${installationTime === 'afternoon'
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                                }`}
                                        >
                                            <span className="text-lg">🌤️</span> Tarde
                                        </button>
                                    </div>
                                    {errors.installationTime && (
                                        <div className="flex items-center gap-1 mt-1.5 text-red-600">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="text-xs font-semibold">{errors.installationTime}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Date Selection */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-900">
                                        Fecha sugerida para la Instalación *
                                    </label>
                                    <input
                                        type="date"
                                        min={getMinInstallationDate()}
                                        value={installationDate}
                                        onChange={(e) => {
                                            setInstallationDate(e.target.value);
                                            if (errors.installationDate) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.installationDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white hover:border-gray-400"
                                    />
                                    {errors.installationDate && (
                                        <div className="flex items-center gap-1 mt-1.5 text-red-600">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="text-xs font-semibold">{errors.installationDate}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Confirmation Message */}
                                {installationTime && installationDate && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                                        <span className="text-lg flex-shrink-0">✓</span>
                                        <div>
                                            <p className="text-sm font-semibold text-green-900">Fecha agendada</p>
                                            <p className="text-sm text-green-700 mt-1">
                                                {installationTime === 'morning' ? 'Mañana 🌅' : 'Tarde 🌤️'} - {new Date(installationDate).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 6: Review */}
                        {currentStep === 6 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 pb-2.5 border-b border-gray-200">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <h2 className="text-sm font-semibold text-gray-900">Confirmar</h2>
                                </div>

                                {/* Personal Data Review */}
                                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-1.5">
                                    <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
                                        <span className="text-base">👤</span> Datos Personales
                                    </h3>
                                    <ReviewField
                                        label="Tipo de Cliente"
                                        value={formData.customerType === CustomerTypeEnum.NATURAL ? 'Persona Natural' : 'Persona Jurídica'}
                                    />
                                    <ReviewField label="Nombre" value={formData.fullName} />
                                    <ReviewField label="Email" value={formData.email} />
                                    <ReviewField label="Teléfono" value={formData.phone} />
                                    {(formData.mobileNumbers || []).length > 0 && (
                                        <ReviewField
                                            label="Otros teléfonos"
                                            value={(formData.mobileNumbers || []).join(', ')}
                                        />
                                    )}
                                    <ReviewField
                                        label="Documento"
                                        value={`${formData.documentType} ${formData.documentNumber}`}
                                    />
                                    <ReviewField
                                        label="Nacimiento"
                                        value={
                                            formData.birthDate instanceof Date
                                                ? formData.birthDate.toLocaleDateString('es-CO')
                                                : ''
                                        }
                                    />
                                </div>

                                {/* Service Data Review */}
                                <div className="bg-green-50 rounded-lg p-3 border border-green-100 space-y-1.5">
                                    <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
                                        <span className="text-base">🏠</span> Tu Domicilio
                                    </h3>
                                    <ReviewField label="Dirección" value={formData.serviceData?.address} />
                                    <ReviewField
                                        label="Ubicación"
                                        value={`${formData.serviceData?.city}, ${formData.serviceData?.department}`}
                                    />
                                    <ReviewField label="Barrio" value={formData.serviceData?.neighborhood} />
                                    <ReviewField
                                        label="Vivienda"
                                        value={`${formData.serviceData?.residenceType} #${formData.serviceData?.residenceNumber}`}
                                    />
                                    <ReviewField
                                        label="Referencia"
                                        value={formData.serviceData?.referencePoint}
                                    />
                                </div>

                                {/* Location Review */}
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100 space-y-1.5">
                                    <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
                                        <span className="text-base">📍</span> Coordenadas
                                    </h3>
                                    <ReviewField
                                        label="Latitud"
                                        value={formData.serviceData?.location?.latitude?.toFixed(6)}
                                    />
                                    <ReviewField
                                        label="Longitud"
                                        value={formData.serviceData?.location?.longitude?.toFixed(6)}
                                    />
                                </div>

                                {/* Installation Review */}
                                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100 space-y-1.5">
                                    <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
                                        <span className="text-base">📅</span> Instalación Agendada
                                    </h3>
                                    <ReviewField
                                        label="Horario"
                                        value={installationTime === 'morning' ? 'Mañana 🌅' : installationTime === 'afternoon' ? 'Tarde 🌤️' : 'No especificado'}
                                    />
                                    <ReviewField
                                        label="Fecha"
                                        value={installationDate ? new Date(installationDate).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'No especificada'}
                                    />
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-blue-800">
                                    <p className="font-semibold mb-1">✓ Todo está listo</p>
                                    <p className="text-xs">Revisa la información y continúa para completar tu registro</p>
                                </div>
                            </div>
                        )}

                        {/* Step 7: Welcome Screen */}
                        {currentStep === 7 && (
                            <div className="space-y-3">
                                {/* Success Icon */}
                                <div className="flex justify-center mb-6">
                                    <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                                        <CheckCircle2 className="w-8 h-8 text-white" />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="text-center mb-6">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                        ¡Bienvenido!
                                    </h2>
                                    <p className="text-gray-600 text-sm sm:text-base">
                                        Tu registro ha sido completado exitosamente
                                    </p>
                                </div>


                                {/* Message Box */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                                    <div className="flex items-start gap-3">
                                        <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-2">Próximos pasos</h3>
                                            <p className="text-sm text-gray-700 leading-relaxed">

                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Reminder to wait for contact - ELEGANT */}
                                <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-center space-y-4 shadow-2xl border border-slate-700">
                                    <div className="text-5xl">😎🤙</div>
                                    <div className="space-y-1.5">
                                        <h3 className="text-xl font-bold text-white">
                                            Pendiente al celular
                                        </h3>
                                        <p className="text-sm text-slate-200">
                                            En cualquier momento te contactaremos
                                        </p>
                                    </div>
                                    <div className="pt-3 border-t border-slate-700">
                                        <div className="flex items-center justify-center gap-2 text-sm text-slate-300">
                                            <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-600 rounded-full">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
                                                </svg>
                                            </span>
                                            Llamada o WhatsApp
                                        </div>
                                    </div>
                                </div>


                                {/* Day of Installation - What to Prepare */}
                                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-5 space-y-4">
                                    <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                                        <span className="text-xl">🔧</span> Día de la instalación, ten listo esto:
                                    </h3>

                                    <div className="space-y-3">
                                        {/* Item 1 - ID */}
                                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-orange-100">
                                            <span className="text-lg flex-shrink-0">🆔</span>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">Cédula en mano</p>
                                                <p className="text-xs text-gray-600 mt-0.5">Y todos los datos del titular del servicio</p>
                                            </div>
                                        </div>

                                        {/* Item 2 - First Recharge */}
                                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-orange-100">
                                            <span className="text-lg flex-shrink-0">💰</span>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">Tu primera recarga</p>
                                                <p className="text-xs text-gray-600 mt-0.5"><b></b>Debes tener <b>Disponible</b> a la mano el dinero necesario para comprar tu primer paquete de navegación</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Important Note */}
                                    <div className="bg-blue-50 border-l-4 border-blue-400 rounded p-3">
                                        <p className="text-xs text-blue-900 leading-relaxed">
                                            <span className="font-bold">IMPORTANTE:</span> La instalación es totalmente GRATIS. Apenas el técnico termine y veas que el internet está volando 🚀, ¡haces tu recarga y a disfrutar!
                                        </p>
                                    </div>
                                </div>



                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                                    <p className="font-semibold">✓ Registro completado con éxito</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="fixed sm:relative bottom-0 left-0 right-0 sm:flex sm:flex-row gap-2.5 sm:gap-3 mt-auto pt-3 border-t sm:border-t-0 sm:pt-0 bg-white px-4 sm:px-0 pb-4 sm:pb-0 z-40">
                        {/* Mobile Layout */}
                        <div className="sm:hidden flex gap-2.5 w-full">
                            {currentStep < 7 ? (
                                <button
                                    onClick={handleNextStep}
                                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 active:scale-95 shadow-md hover:shadow-lg transition transform duration-200"
                                >
                                    <span>Siguiente</span>
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition transform duration-200"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    Ir a Iniciar Sesión
                                </button>
                            )}
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden sm:flex gap-3 w-full">
                            {currentStep < 7 ? (
                                <button
                                    onClick={handleNextStep}
                                    className="flex-1 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 hover:bg-blue-700 active:scale-95 shadow-md hover:shadow-lg transition transform duration-200"
                                >
                                    Siguiente
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="flex-1 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold text-sm hover:from-emerald-700 hover:to-green-700 flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-95 transition transform duration-200"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    Ir a Iniciar Sesión
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Helper Components
interface FormFieldProps {
    label: string;
    value: string | number;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    error?: string;
    placeholder?: string;
    type?: string;
    step?: string;
    disabled?: boolean;
    options?: Array<{ label: string; value: string }>;
}

function FormField({
    label,
    value,
    onChange,
    error,
    placeholder,
    type = 'text',
    step,
    disabled = false,
    options,
}: FormFieldProps) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>
            {type === 'select' && options ? (
                <select
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed bg-white hover:border-gray-400"
                >
                    <option value="">Selecciona una opción</option>
                    {options.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    inputMode={type === 'tel' ? 'tel' : type === 'email' ? 'email' : undefined}
                    step={step}
                    disabled={disabled}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed bg-white hover:border-gray-400 placeholder-gray-400"
                />
            )}
            {error && (
                <div className="flex items-center gap-1 mt-1.5 text-red-600">
                    <span className="text-xs font-semibold">⚠ {error}</span>
                </div>
            )}
        </div>
    );
}

function ReviewField({ label, value }: { label: string; value?: string }) {
    return (
        <div className="flex justify-between items-center py-1.5 text-sm">
            <span className="font-medium text-gray-700">{label}</span>
            <span className="text-gray-900 font-semibold text-right flex-1 ml-3">{value || '—'}</span>
        </div>
    );
}
